import { Box, Button, FormControlLabel, IconButton, Radio, RadioGroup, Typography } from '@mui/material';
import { TransferSource } from 'apis/business/agent/action/types';
import { TeamMember } from 'apis/business/setting/team/types';
import Input from 'components/Common/Input';
import Select, { Option } from 'components/Common/Select';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Control, get, useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';
import OperatorTransferEntry from 'pages/Business/Agent/Configure/Behavior/components/GlobalActions/Components/OperatorTransferEntry';
import {
  applyTransferTypeSnapshot,
  captureTransferTypeSnapshot,
  CLEARED_TRANSFER_SOURCE,
  getDefaultTransferTypeState,
  getInitialExpandedOperatorIndex,
  getSourcesForTransferType,
  getTransferModeOptions,
  isTransferSourceComplete,
  RINGING_STRATEGY_OPTIONS,
  TransferTypeFormSnapshot
} from 'pages/Business/Agent/Configure/Behavior/components/GlobalActions/Components/transferCallUtils';

const ROOT = 'transfer_call_details';
const TRANSFER_TYPE_OPTIONS = [
  { label: 'Operator', value: 'operator' },
  { label: 'Group', value: 'group' }
] as const;

type OnboardingOperatorDetail = {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
  extension?: string;
  sip_uri?: string;
  working_hours?: TeamMember['working_hours'];
};

export function createEmptyTransferCallConfig() {
  return {
    transfer_type: 'operator' as const,
    transfer_mode: 'warm' as const,
    transfer_strategy: undefined as string | undefined,
    trigger: '',
    sources: [{ ...CLEARED_TRANSFER_SOURCE }]
  };
}

function mapOperatorDetailsToOperators(operatorDetails: OnboardingOperatorDetail[] = []): TeamMember[] {
  return operatorDetails
    .map((op, index) => ({
      id: (op.id ?? index) as number,
      first_name: op.name || '',
      last_name: '',
      job_title: '',
      email: op.email || '',
      phone_number: op.phone || '',
      sip_uri: op.sip_uri || '',
      extension: op.extension || '',
      working_hours: op.working_hours,
      holidays: []
    }))
    .filter((op) => Boolean(op.first_name) && Boolean(op.phone_number || op.sip_uri || op.extension));
}

const infoBannerSx = {
  mt: 0.5,
  mb: 1,
  px: 2,
  py: 1.5,
  fontSize: 14,
  borderRadius: 2,
  border: '1px solid #D97706',
  bgcolor: '#FFFBEB',
  color: '#D97706'
};

function TransferGroupCard({
  index,
  parent,
  control,
  operatorOptions,
  operatorsData,
  showRemove,
  onRemove
}: {
  index: number;
  parent: string;
  control: Control<any>;
  operatorOptions: Option[];
  operatorsData: TeamMember[];
  showRemove: boolean;
  onRemove: () => void;
}) {
  const [expandedOperatorIndex, setExpandedOperatorIndex] = useState<number | null>(null);
  const { watch, setValue, getValues } = useFormContext();
  const { errors, isSubmitted } = useFormState({ control });
  const transferTypeSnapshots = useRef<Partial<Record<'operator' | 'group', TransferTypeFormSnapshot>>>({});

  const transferType = watch(`${parent}.transfer_type`) || 'operator';
  const transferMode = watch(`${parent}.transfer_mode`);
  const transferStrategy = watch(`${parent}.transfer_strategy`);
  const operatorTargetType = watch(`${parent}.sources.0.target_type`);
  const groupSources = watch(`${parent}.sources`) as TransferSource[] | undefined;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: `${parent}.sources`
  });

  const sourcesRootError = get(errors, `${parent}.sources.root.message`);

  useEffect(() => {
    if (!watch(`${parent}.transfer_type`)) {
      setValue(`${parent}.transfer_type`, 'operator', { shouldDirty: false });
    }
    if (!watch(`${parent}.transfer_mode`)) {
      setValue(`${parent}.transfer_mode`, 'warm', { shouldDirty: false });
    }
    if (watch(`${parent}.trigger`) === undefined) {
      setValue(`${parent}.trigger`, '', { shouldDirty: false });
    }
    const currentSources = watch(`${parent}.sources`);
    if (!Array.isArray(currentSources) || currentSources.length === 0) {
      replace([{ ...CLEARED_TRANSFER_SOURCE }]);
    }
  }, [parent, replace, setValue, watch]);

  useEffect(() => {
    if (transferType !== 'group') {
      setExpandedOperatorIndex(null);
      return;
    }

    setExpandedOperatorIndex((current) => {
      if (current !== null) return current;
      return getInitialExpandedOperatorIndex(groupSources || []);
    });
  }, [transferType, groupSources, fields.length]);

  useEffect(() => {
    if (transferType === 'group') {
      if (transferMode !== 'warm') {
        setValue(`${parent}.transfer_mode`, 'warm', { shouldDirty: false });
      }
      if (!watch(`${parent}.transfer_strategy`)) {
        setValue(`${parent}.transfer_strategy`, 'simultaneous', { shouldDirty: false });
      }
    }
  }, [transferType, transferMode, parent, setValue, watch]);

  useEffect(() => {
    if (transferType !== 'operator') return;
    if (operatorTargetType === 'sip' && transferMode !== 'cold') {
      setValue(`${parent}.transfer_mode`, 'cold', { shouldDirty: false });
    }
    if (operatorTargetType === 'extension' && transferMode !== 'warm') {
      setValue(`${parent}.transfer_mode`, 'warm', { shouldDirty: false });
    }
  }, [operatorTargetType, transferType, transferMode, parent, setValue]);

  const handleTransferTypeChange = (value: string) => {
    if (value === transferType) return;

    const currentDetails = (getValues(parent) as Record<string, unknown>) || {};
    transferTypeSnapshots.current[transferType as 'operator' | 'group'] = captureTransferTypeSnapshot(
      currentDetails,
      expandedOperatorIndex
    );

    setValue(`${parent}.transfer_type`, value);

    const cachedSnapshot = transferTypeSnapshots.current[value as 'operator' | 'group'];
    const snapshot = cachedSnapshot ?? getDefaultTransferTypeState(value);

    applyTransferTypeSnapshot(snapshot, parent, setValue, replace);

    if (value === 'group') {
      setValue(`${parent}.transfer_strategy`, snapshot.transfer_strategy || 'simultaneous', { shouldDirty: false });
      setValue(`${parent}.transfer_mode`, 'warm', { shouldDirty: false });
      setExpandedOperatorIndex(snapshot.expandedOperatorIndex ?? getInitialExpandedOperatorIndex(snapshot.sources));
    } else {
      setExpandedOperatorIndex(null);
    }
  };

  const handleAddGroupOperator = () => {
    setExpandedOperatorIndex(fields.length);
    append(getSourcesForTransferType('operator')[0]);
  };

  const handleRemoveGroupOperator = (sourceIndex: number) => {
    remove(sourceIndex);
    setExpandedOperatorIndex((current) => {
      if (current === null) return null;
      if (current === sourceIndex) return Math.max(0, sourceIndex - 1);
      if (current > sourceIndex) return current - 1;
      return current;
    });
  };

  const getGroupOperatorVariant = (sourceIndex: number): 'expanded' | 'summary' => {
    const entry = groupSources?.[sourceIndex];
    const isComplete = isTransferSourceComplete(entry);
    const isActive = expandedOperatorIndex === sourceIndex;

    if (isComplete && !isActive) return 'summary';
    return 'expanded';
  };

  const transferModeOptions = getTransferModeOptions(transferType === 'operator' ? operatorTargetType : undefined);
  const isTransferModeDisabled =
    transferType === 'group' || (transferType === 'operator' && ['sip', 'extension'].includes(operatorTargetType));

  return (
    <Box
      sx={{
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        p: 2,
        mb: 2
      }}
    >
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={1.5}>
        <Typography fontWeight={600} fontSize={15}>
          {transferType === 'operator' ? `Operator-${index + 1}` : `Group-${index + 1}`}
        </Typography>
        {showRemove && (
          <IconButton size='small' onClick={onRemove} aria-label='Remove group'>
            <RiDeleteBin6Line size={18} color='#D32F2F' />
          </IconButton>
        )}
      </Box>

      {transferType === 'operator' && (
        <Typography sx={infoBannerSx}>
          If operator doesn&apos;t answers the call, the AI agent will take a note and notify.
        </Typography>
      )}

      <RadioGroup row value={transferType} onChange={(e) => handleTransferTypeChange(e.target.value)} sx={{ mb: 1 }}>
        {TRANSFER_TYPE_OPTIONS.map((option) => (
          <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
        ))}
      </RadioGroup>

      {transferType === 'group' && (
        <>
          <Select
            name={`${parent}.transfer_strategy`}
            label='Ringing Strategy'
            control={control}
            options={[...RINGING_STRATEGY_OPTIONS]}
            helperText={
              transferStrategy === 'simultaneous'
                ? 'All selected operators are called at the same time. The first operator to answer is connected to the caller, and ringing stops for everyone else.'
                : transferStrategy === 'sequential'
                  ? "Calls are placed one operator at a time in the order listed below. If the first operator doesn't answer, the call is automatically offered to the next operator, and continues until someone answers or the list is exhausted."
                  : ''
            }
            fullWidth
            sx={{ mb: 2 }}
          />
          <Typography sx={infoBannerSx}>
            In case of multiple operators, the call will ring simultaneously for all assigned operators. The first
            operator to answer the call will be connected, and the call will automatically disconnect for all other
            operators. If no operator answers the call, the AI agent will take a note and notify.
          </Typography>
        </>
      )}

      {operatorOptions.length === 0 && (
        <Typography color='text.secondary' fontSize={14} mb={2}>
          Add operators with a name and at least one contact method (phone, SIP, or extension) in the Member Details
          step to configure call transfer.
        </Typography>
      )}

      {transferType === 'operator' && (
        <OperatorTransferEntry
          index={0}
          parent={parent}
          control={control}
          operatorOptions={operatorOptions}
          operatorsData={operatorsData}
          onScrollEnd={() => undefined}
          label='Operator-1'
        />
      )}

      {transferType === 'group' && (
        <>
          {fields.map((field, sourceIndex) => {
            const excludedOperatorIds = (groupSources || [])
              .filter((_, i) => i !== sourceIndex)
              .map((source) => source?.target_id)
              .filter((id): id is string | number => id !== undefined && id !== null && id !== '');

            return (
              <OperatorTransferEntry
                key={field.id}
                index={sourceIndex}
                parent={parent}
                control={control}
                operatorOptions={operatorOptions}
                operatorsData={operatorsData}
                onScrollEnd={() => undefined}
                label={`Operator-${sourceIndex + 1}`}
                variant={getGroupOperatorVariant(sourceIndex)}
                groupTransferMode={transferMode || 'warm'}
                showRemove={fields.length > 1}
                onRemove={() => handleRemoveGroupOperator(sourceIndex)}
                onEdit={() => setExpandedOperatorIndex(sourceIndex)}
                excludedOperatorIds={excludedOperatorIds}
              />
            );
          })}

          <Button
            variant='outlined'
            fullWidth
            onClick={handleAddGroupOperator}
            sx={{ mb: isSubmitted && sourcesRootError ? 1 : 2 }}
          >
            + Add Operator
          </Button>
          {isSubmitted && sourcesRootError && (
            <Typography color='error' fontSize={12} sx={{ mb: 2, px: 1.75 }}>
              {String(sourcesRootError)}
            </Typography>
          )}
        </>
      )}

      <Select
        name={`${parent}.transfer_mode`}
        label='Transfer Mode'
        control={control}
        disabled={isTransferModeDisabled}
        options={transferModeOptions}
        fullWidth
        sx={{ mb: 2 }}
      />

      {transferType === 'group' && (
        <Typography sx={{ ...infoBannerSx, mb: 2 }}>
          Note: Only Warm Transfer is supported for Group Calling. A summary is not provided to the operator before the
          call is connected.
        </Typography>
      )}

      <Input
        name={`${parent}.trigger`}
        label={`When to transfer to this ${transferType === 'operator' ? 'operator' : 'group'}`}
        control={control}
        placeholder={
          transferType === 'group'
            ? 'e.g: Caller asks about billing, insurance claims.'
            : 'e.g: Caller asks about a refund'
        }
        multiline
        rows={3}
        fullWidth
      />
    </Box>
  );
}

export default function TransferConfig() {
  const { control, watch } = useFormContext();
  const operatorDetails = watch('operator_details') as OnboardingOperatorDetail[] | undefined;

  const { fields, append, remove } = useFieldArray({
    control,
    name: ROOT
  });

  const operatorsData = useMemo(() => mapOperatorDetailsToOperators(operatorDetails), [operatorDetails]);

  const operatorOptions: Option[] = useMemo(
    () =>
      operatorsData.map((operator) => ({
        label: operator.first_name,
        value: operator.id
      })),
    [operatorsData]
  );

  useEffect(() => {
    if (fields.length === 0) {
      append(createEmptyTransferCallConfig());
    }
  }, [append, fields.length]);

  return (
    <Box display='flex' flexDirection='column'>
      {fields.map((field, index) => (
        <TransferGroupCard
          key={field.id}
          index={index}
          parent={`${ROOT}.${index}`}
          control={control}
          operatorOptions={operatorOptions}
          operatorsData={operatorsData}
          showRemove={fields.length > 1}
          onRemove={() => remove(index)}
        />
      ))}

      <Button variant='outlined' fullWidth onClick={() => append(createEmptyTransferCallConfig())}>
        + Add
      </Button>
    </Box>
  );
}
